import { CloseCircleOutlined, FilterOutlined } from '@ant-design/icons'
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  FormControl,
  FormControlLabel,
  Grid2 as Grid,
  IconButton,
  Menu,
  MenuItem,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import Paper from 'components/@extended/Paper'
import ButtonActionBar from 'components/ButtonActionBar'
import MultiOrganizationSelector from 'components/MultiOrganizationSelector'
import MultiPeriodSelector from 'components/MultiPeriodSelector'
import SearchInput from 'components/SearchInput'
import SimpleTitle from 'components/SimpleTitle'
import { useAccruals, useUpdateAccrualsVisibility } from 'hooks/useAccruals'
import useAuth from 'hooks/useAuth'
import { useFetchAllLedgerAccounts } from 'hooks/useLedgerAccounts'
import useLocalStorage from 'hooks/useLocalStorage'
import { usePeriods } from 'hooks/usePeriods'
import React, { Suspense, useCallback, useEffect, useState, useTransition } from 'react'
import type { AccrualsQueryParams } from 'types/accruals'
import { ThemeMode } from 'types/config'
import type { SortParams } from 'types/default-params'
import type { LedgerAccount } from 'types/ledger-account'
import type { Period } from 'types/periods'
import { createMergedIdLabelMap } from 'types/periods'
import type { UserRole } from 'types/role'
import AccrualDetails from './AccrualDetails'
import VisibilityConfirmationDialog from './VisibilityConfirmationDialog'

interface Column {
  id: string
  label: string
  width: number
  format?: (row: any) => React.ReactNode
}

const ManageAccruals: React.FC = () => {
  const theme = useTheme()
  const { token, profile } = useAuth()
  const [searchTerm, setSearchTerm] = useState('')
  const [page, setPage] = useLocalStorage<number>('accruals-page', 0, profile?.user?.id)
  const [rowsPerPage, setRowsPerPage] = useLocalStorage<number>('accruals-rows-per-page', 100, profile?.user?.id)
  const [sort, setSort] = useLocalStorage<string>('accruals-sort', 'createdAt', profile?.user?.id)
  const [order, setOrder] = useLocalStorage<string>('accruals-order', 'desc', profile?.user?.id)
  const [sortParams, setSortParams] = useState<SortParams[]>([])
  const [selectedOrganizations, setSelectedOrganizations] = useLocalStorage<string[]>(
    'accruals-selected-organizations',
    [],
    profile?.user?.id
  )
  const [selectedPeriods, setSelectedPeriods] = useLocalStorage<string[]>(
    'accruals-selected-periods',
    [],
    profile?.user?.id
  )
  const [periods, setPeriods] = useState<Period[]>([])
  const [isPending, startTransition] = useTransition()
  const [periodLabels, setPeriodLabels] = useState<Record<string, string>>({})
  const [status, setStatus] = useLocalStorage<AccrualsQueryParams['status']>(
    'accruals-status',
    'Approved',
    profile?.user?.id
  )
  const [selectedAccrual, setSelectedAccrual] = useState<any | null>(null)
  const [openDialog, setOpenDialog] = useState(false)
  const [periodsInitialized, setPeriodsInitialized] = useState(false)
  const [isInitialized, setIsInitialized] = useState(false)
  const [selectedItems, setSelectedItems] = useState<string[]>([])
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false)
  const [moreFiltersAnchor, setMoreFiltersAnchor] = useState<null | HTMLElement>(null)
  const [showVisible, setShowVisible] = useLocalStorage<boolean>('accruals-show-visible', true, profile?.user?.id)
  const [showHidden, setShowHidden] = useLocalStorage<boolean>('accruals-show-hidden', false, profile?.user?.id)
  // const [isQueueing, setIsQueueing] = useState(false)
  // const { enqueueSnackbar } = useSnackbar()

  const hoverColor =
    theme.palette.mode === ThemeMode.DARK ? theme.palette.primary.darker : theme.palette.primary.lighter

  const { ledgerAccounts, isLoading: isLoadingLedgerAccounts } = useFetchAllLedgerAccounts(
    selectedOrganizations,
    token!,
    selectedOrganizations.length > 0 && !!token
  )

  const { periods: fetchedPeriods, isLoadingPeriods } = usePeriods(
    token!,
    selectedOrganizations[0] || '',
    !!token && selectedOrganizations.length > 0
  )

  const { updateVisibility, error: visibilityError } = useUpdateAccrualsVisibility(token!)

  useEffect(() => {
    setSortParams([{ field: sort, order }])
  }, [sort, order])

  useEffect(() => {
    if (fetchedPeriods) {
      setPeriods(fetchedPeriods.periods || [])

      // Select the first root period if available
      if (
        fetchedPeriods.periods &&
        fetchedPeriods.periods.length > 0 &&
        selectedPeriods.length === 0 &&
        !periodsInitialized
      ) {
        setPeriodsInitialized(true)
      }
    }
  }, [fetchedPeriods])

  useEffect(() => {
    if (fetchedPeriods && fetchedPeriods.periods.length > 0) {
      const mergedLabelMap = createMergedIdLabelMap(fetchedPeriods.periods)
      setPeriodLabels(mergedLabelMap)
    }
  }, [fetchedPeriods])

  useEffect(() => {
    if (profile && profile.roles.length > 0 && !isInitialized) {
      const allOrganizationIds = profile.roles.map((role) => role.organization.id)
      if (selectedOrganizations.length === 0) {
        setSelectedOrganizations(allOrganizationIds)
      }
      setIsInitialized(true)
    }
  }, [profile, isInitialized])

  // Determine visibility filter based on show toggles
  const getVisibilityFilter = (): boolean | undefined => {
    if (showVisible && !showHidden) return true // Only visible
    if (!showVisible && showHidden) return false // Only hidden
    return undefined // Both or neither - show all
  }

  const queryParams: AccrualsQueryParams = {
    organizationIds: selectedOrganizations,
    periodIds: selectedPeriods,
    status,
    searchTerm,
    pagination: {
      page: page + 1,
      perPage: rowsPerPage,
    },
    sort: sortParams,
    isVisible: getVisibilityFilter(),
  }

  const {
    accruals = [],
    pagination,
    isLoading,
    isError,
    refetch,
  } = useAccruals(
    token!,
    queryParams,
    !!token && selectedOrganizations.length > 0 && selectedPeriods.length > 0 && isInitialized
  )

  // Handle sorting
  const handleSort = (property: string) => {
    const isAscending = sort === property && order === 'asc'
    setOrder(isAscending ? 'desc' : 'asc')
    setSort(property)
  }

  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null, newPage: number) => {
    startTransition(() => {
      setPage(newPage)
    })
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    startTransition(() => {
      setRowsPerPage(Number.parseInt(event.target.value, 10))
      setPage(0)
    })
  }

  const handleSearch = useCallback((value: string) => {
    setSearchTerm(value)
  }, [])

  const organizationLabels: Record<string, string> =
    profile?.roles?.reduce(
      (acc: Record<string, string>, role: UserRole) => {
        acc[role.organization.id] = role.organization.name
        return acc
      },
      {} as Record<string, string>
    ) ?? {}

  const ledgerAccountLabels: Record<string, string> =
    ledgerAccounts?.reduce(
      (acc: Record<string, string>, ledgerAccount: LedgerAccount) => {
        acc[ledgerAccount.id] = ledgerAccount.label
        return acc
      },
      {} as Record<string, string>
    ) || {}

  // const handleViewAccrual = (accrual: any) => {
  //   setSelectedAccrual(accrual)
  //   setOpenDialog(true)
  // }

  const handleCloseDialog = () => {
    setOpenDialog(false)
    setSelectedAccrual(null)
  }

  // Helper function to find a period by ID (searches recursively)
  const findPeriodById = (periodId: string, periodsToSearch: Period[] = periods): Period | null => {
    for (const period of periodsToSearch) {
      if (period.id === periodId) {
        return period
      }
      if (period.children && period.children.length > 0) {
        const found = findPeriodById(periodId, period.children)
        if (found) return found
      }
    }
    return null
  }

  // Helper function to detect duplicate line items and format them with dates if needed
  const formatLineItemWithDates = (row: any): string => {
    if (row.lineItemCount) {
      return `(${row.lineItemCount}) Line Items`
    }

    const baseLabel = `${row.allocationTableName} | ${row.allocationRowName}`

    // Find all accruals with the same userId, periodId, and line item label
    const duplicates = accruals.filter(
      (a) =>
        a.userId === row.userId &&
        a.periodId === row.periodId &&
        `${a.allocationTableName} | ${a.allocationRowName}` === baseLabel &&
        a.id !== row.id // Don't count itself
    )

    // If there are duplicates, append the plan date range
    if (duplicates.length > 0 && (row.planEffectiveDate || row.planEndDate)) {
      const formatDate = (dateStr: string | undefined) => {
        if (!dateStr) return null
        const date = new Date(dateStr)
        const month = (date.getMonth() + 1).toString()
        const day = date.getDate().toString()
        const year = date.getFullYear().toString().slice(-2) // Last 2 digits of year
        return `${month}/${day}/${year}`
      }

      const startDate = formatDate(row.planEffectiveDate)
      let endDate = formatDate(row.planEndDate)

      // If no plan end date, use the period end date
      if (!endDate && row.periodId) {
        const period = findPeriodById(row.periodId)
        if (period && period.endDate) {
          endDate = formatDate(period.endDate)
        }
      }

      if (startDate && endDate) {
        return `${baseLabel} (${startDate} - ${endDate})`
      } else if (startDate) {
        return `${baseLabel} (${startDate} - Present)`
      }
    }

    return baseLabel
  }

  const columns: Column[] = [
    {
      id: 'select',
      label: '',
      width: 50,
      format: (row: any) => (
        <Checkbox checked={selectedItems.includes(row.id)} onChange={() => handleSelectItem(row.id)} size="small" />
      ),
    },
    {
      id: 'userFirstName',
      label: 'First Name',
      width: 100,
    },
    {
      id: 'userLastName',
      label: 'Last Name',
      width: 100,
    },
    {
      id: 'organizationName',
      label: 'Team',
      width: 150,
      format: (row) => row.organizationName || '-',
    },
    {
      id: 'periodId',
      label: 'Period',
      width: 150,
      format: (row) => periodLabels[row.periodId] || row.periodId,
    },
    {
      id: 'status',
      label: 'Status',
      width: 100,
      format: (row) => row.status.charAt(0).toUpperCase() + row.status.slice(1),
    },
    {
      id: 'lineItem',
      label: 'Line Item',
      width: 200,
      format: (row) => formatLineItemWithDates(row),
    },
    {
      id: 'total',
      label: 'Balance',
      width: 100,
      format: (row) =>
        row.proratedPayoutAmount !== null
          ? new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD',
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }).format(Number(row.proratedPayoutAmount))
          : '-',
    },
    // {
    //   id: 'actions',
    //   label: 'Actions',
    //   width: 100,
    //   format: (row) => (
    //     <IconButton onClick={() => handleViewAccrual(row)}>
    //       <EyeOutlined />
    //     </IconButton>
    //   ),
    // },
  ]

  const handleOrganizationChange = (newSelectedOrganizations: string[]) => {
    startTransition(() => {
      setSelectedOrganizations(newSelectedOrganizations)
      setPage(0)
    })
  }

  const handlePeriodChange = (newSelectedPeriods: string[]) => {
    startTransition(() => {
      setSelectedPeriods(newSelectedPeriods)
      setPage(0)
    })
  }

  const handleShowVisibleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShowVisible(event.target.checked)
    setPage(0)
  }

  const handleShowHiddenChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShowHidden(event.target.checked)
    setPage(0)
  }

  const handleMoreFiltersClick = (event: React.MouseEvent<HTMLElement>) => {
    setMoreFiltersAnchor(event.currentTarget)
  }

  const handleMoreFiltersClose = () => {
    setMoreFiltersAnchor(null)
  }

  const handleSelectItem = (accrualId: string) => {
    setSelectedItems((prev) =>
      prev.includes(accrualId) ? prev.filter((id) => id !== accrualId) : [...prev, accrualId]
    )
  }

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedItems(accruals.map((accrual) => accrual.id))
    } else {
      setSelectedItems([])
    }
  }

  const handlePublishSelected = () => {
    setOpenConfirmationDialog(true)
  }

  // Check if all selected items have the same visibility state
  const getSelectedItemsVisibilityState = (): 'mixed' | 'visible' | 'hidden' => {
    const selectedAccruals = accruals.filter((accrual) => selectedItems.includes(accrual.id))
    if (selectedAccruals.length === 0) return 'mixed'

    const allVisible = selectedAccruals.every((accrual) => accrual.isVisible === true)
    const allHidden = selectedAccruals.every((accrual) => accrual.isVisible === false)

    if (allVisible) return 'visible'
    if (allHidden) return 'hidden'
    return 'mixed'
  }

  const handleConfirmVisibilityChange = async (makeVisible: boolean) => {
    try {
      // Get the root organization ID from the first selected organization
      // or use the first organization if none selected
      const rootOrganizationId = selectedOrganizations[0] || profile?.roles[0]?.organization?.id
      const organizationId = selectedOrganizations[0] || profile?.roles[0]?.organization?.id

      if (!rootOrganizationId || !organizationId) {
        console.error('No organization selected')
        return
      }

      await updateVisibility({
        organizationId,
        rootOrganizationId,
        isVisible: makeVisible,
        accrualIds: selectedItems,
      })

      // After successful operation, clear selections and close dialog
      setSelectedItems([])
      setOpenConfirmationDialog(false)

      // Refetch the data to show updated visibility
      refetch()

      // Optionally show success message or refetch data
      // enqueueSnackbar('Visibility updated successfully', { variant: 'success' })
    } catch (error) {
      console.error('Failed to update visibility:', error)
      // enqueueSnackbar('Failed to update visibility', { variant: 'error' })
    }
  }

  const handleCancelVisibilityChange = () => {
    setOpenConfirmationDialog(false)
  }

  return (
    <>
      <Grid container pb={2}>
        <Grid size={{ xs: 12 }}>
          <SimpleTitle title="Progress" />
        </Grid>
      </Grid>
      <Grid container spacing={2} mb={3} alignItems="center">
        <Grid size={{ xs: 3 }}>
          <SearchInput
            id="accrual-search-input"
            key="accrual-search-input"
            onSearch={handleSearch}
            initialValue={searchTerm}
            labelBackgroundColor={theme.palette.background.default}
          />
        </Grid>
        <Grid size={{ xs: 3 }}>
          <MultiPeriodSelector
            key="period-selector"
            periods={periods}
            selectedPeriods={selectedPeriods}
            handleChange={handlePeriodChange}
            rootPeriodOnly={false}
            sortOrder="asc"
            timeFilter="pastOnly"
            labelBackgroundColor={theme.palette.background.default}
          />
        </Grid>
        <Grid size={{ xs: 4 }}>
          <MultiOrganizationSelector
            key="organization-selector"
            userProfile={profile!}
            selectedOrganizationIds={selectedOrganizations}
            handleChange={handleOrganizationChange}
            labelBackgroundColor={theme.palette.background.default}
          />
        </Grid>
        <Grid size={{ xs: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              variant="outlined"
              onClick={handleMoreFiltersClick}
              startIcon={<FilterOutlined />}
              sx={{ minWidth: 140 }}
            >
              More Filters
            </Button>
            <Tooltip title="Clear Filters">
              <IconButton
                color="primary"
                onClick={() => {
                  setStatus('Approved')
                  setSelectedPeriods([])
                  setSelectedOrganizations([])
                  setSearchTerm('')
                  setShowVisible(true)
                  setShowHidden(false)
                }}
              >
                <CloseCircleOutlined />
              </IconButton>
            </Tooltip>
          </Box>
        </Grid>
      </Grid>

      <Suspense fallback={<CircularProgress />}>
        {isLoadingPeriods || isLoading || isPending || isLoadingLedgerAccounts ? (
          <CircularProgress />
        ) : isError ? (
          <Typography color="error">Error loading accruals</Typography>
        ) : visibilityError ? (
          <Typography color="error">Error updating visibility: {visibilityError}</Typography>
        ) : (
          <Box>
            <TableContainer>
              <Table stickyHeader aria-label="sticky table" size="small" component={Paper}>
                <TableHead
                  sx={{
                    '& th': {
                      borderTop: `1px solid ${theme.palette.divider}`,
                      borderBottom: `2px solid ${theme.palette.divider} !important`,
                    },
                  }}
                >
                  <TableRow>
                    {columns.map((column) => (
                      <TableCell
                        key={column.id}
                        style={{ width: column.width, minWidth: column.width }}
                        sx={{ position: 'sticky !important' }}
                      >
                        {column.id === 'select' ? (
                          <Checkbox
                            checked={selectedItems.length > 0 && selectedItems.length === accruals.length}
                            indeterminate={selectedItems.length > 0 && selectedItems.length < accruals.length}
                            onChange={handleSelectAll}
                            disabled={accruals.length === 0}
                            size="small"
                          />
                        ) : column.id === 'actions' ||
                          column.id === 'periodId' ||
                          column.id === 'lineItem' ||
                          column.id === 'account' ||
                          column.id === 'total' ? (
                          column.label
                        ) : (
                          <TableSortLabel
                            active={sort === column.id}
                            direction={sort === column.id ? (order as 'asc' | 'desc') : 'asc'}
                            onClick={() => handleSort(column.id as string)}
                          >
                            {column.label}
                          </TableSortLabel>
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {accruals.map((accrual) => {
                    const isHidden = !accrual.isVisible
                    return (
                      <TableRow
                        key={accrual.id}
                        role="checkbox"
                        tabIndex={-1}
                        sx={{
                          color: isHidden ? theme.palette.text.disabled : 'inherit',
                          '& .MuiTableCell-root': {
                            color: isHidden ? theme.palette.text.disabled : 'inherit',
                          },
                        }}
                      >
                        {columns.map((column) => (
                          <TableCell key={column.id} style={{ width: column.width }}>
                            {column.format ? column.format(accrual) : accrual[column.id as keyof typeof accrual]}
                          </TableCell>
                        ))}
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[100, 250, 500]}
              component="div"
              count={pagination?.totalItems || 0}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Box>
        )}
      </Suspense>
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogContent>
          {selectedAccrual && (
            <AccrualDetails
              accrual={selectedAccrual}
              organizationLabels={organizationLabels}
              periodLabels={periodLabels}
              ledgerAccountLabels={ledgerAccountLabels}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ m: 2 }}>
          <Button onClick={handleCloseDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* More Filters Menu */}
      <Menu
        anchorEl={moreFiltersAnchor}
        open={Boolean(moreFiltersAnchor)}
        onClose={handleMoreFiltersClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: { minWidth: '280px', mt: 1 },
        }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="overline" sx={{ display: 'block', color: 'text.secondary' }}>
            Visibility
          </Typography>
          <Stack spacing={1}>
            <FormControlLabel
              control={<Switch checked={showVisible} onChange={handleShowVisibleChange} size="small" />}
              label="Show Visible"
              sx={{
                '& .MuiFormControlLabel-label': {
                  fontSize: '0.875rem',
                },
              }}
            />
            <FormControlLabel
              control={<Switch checked={showHidden} onChange={handleShowHiddenChange} size="small" />}
              label="Show Hidden"
              sx={{
                '& .MuiFormControlLabel-label': {
                  fontSize: '0.875rem',
                },
              }}
            />
          </Stack>
        </Box>
        <Divider sx={{ my: 1 }} />
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="overline" sx={{ display: 'block', color: 'text.secondary' }}>
            Approval Status
          </Typography>
          <Stack spacing={1}>
            <FormControl variant="outlined" fullWidth>
              <Select
                fullWidth
                value={status}
                onChange={(e) => setStatus(e.target.value as AccrualsQueryParams['status'])}
                displayEmpty
                MenuProps={{
                  PaperProps: {
                    sx: {
                      '& .MuiMenuItem-root:hover': {
                        backgroundColor: hoverColor,
                      },
                    },
                  },
                }}
                style={{ borderRadius: '21px' }}
              >
                <MenuItem value="Pending">Pending</MenuItem>
                <MenuItem value="Approved">Approved</MenuItem>
                <MenuItem value="Rejected">Rejected</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </Box>
      </Menu>

      <VisibilityConfirmationDialog
        open={openConfirmationDialog}
        onClose={handleCancelVisibilityChange}
        onConfirm={handleConfirmVisibilityChange}
        selectedAccruals={accruals.filter((accrual) => selectedItems.includes(accrual.id))}
        action={getSelectedItemsVisibilityState() === 'visible' ? 'hidden' : 'visible'}
        periodLabels={periodLabels}
      />

      {/* Bottom Action Bar */}
      <ButtonActionBar show={selectedItems.length > 0} maxWidth="900px" justifyContent="space-between">
        <Box display="flex" alignItems="center" gap={2}>
          <Box sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
            {selectedItems.length} item{selectedItems.length > 1 ? 's' : ''} selected
          </Box>
        </Box>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            color="warning"
            startIcon={<CloseCircleOutlined />}
            onClick={() => setSelectedItems([])}
          >
            Cancel
          </Button>
          {getSelectedItemsVisibilityState() !== 'mixed' && (
            <Button variant="outlined" color="primary" onClick={handlePublishSelected}>
              {getSelectedItemsVisibilityState() === 'visible' ? 'Make Hidden' : 'Make Visible'}
            </Button>
          )}
        </Box>
      </ButtonActionBar>
    </>
  )
}

export default ManageAccruals
